import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Tooltip,
  Grid,
  Paper,
  useTheme,
} from "@mui/material";

const HOUR_LABELS = [...Array(24).keys()].map((h) => `${h}:00`);

const getWeightedHour = (data, type) => {
  let numerator = 0;
  let denominator = 0;
  data.forEach((item) => {
    const hour = Number(item.HourOfDay);
    const total = Number(item.TotalEmployees || 0);
    const value = Number(item[type] || 0);
    if (total > 0 && value > 0) {
      const percent = (value / total) * 100;
      numerator += hour * percent;
      denominator += percent;
    }
  });
  return denominator > 0 ? numerator / denominator : null;
};

const EstimatedWorkHoursChart = ({ rawData = [] }) => {
  const theme = useTheme();

  const summary = useMemo(() => {
    const byVertical = {};

    rawData.forEach((item) => {
      const vertical = item.Business_Vertical_Desc;
      if (!byVertical[vertical]) byVertical[vertical] = [];
      byVertical[vertical].push(item);
    });

    return Object.entries(byVertical).map(([vertical, records]) => {
      const inHour = getWeightedHour(records, "CheckIns");
      const outHour = getWeightedHour(records, "CheckOuts");

      return {
        vertical,
        inHour: inHour !== null ? Number(inHour.toFixed(1)) : null,
        outHour: outHour !== null ? Number(outHour.toFixed(1)) : null,
        duration:
          inHour !== null && outHour !== null
            ? Number((outHour - inHour).toFixed(1))
            : null,
      };
    });
  }, [rawData]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Estimated Work Hours (Weighted Average)
      </Typography>

      <Grid container spacing={1}>
        <Grid item size={2.5}>
          <Typography fontWeight="bold">Business Vertical</Typography>
        </Grid>
        <Grid item size={9.5}>
          <Box display="flex">
            {HOUR_LABELS.map((label, idx) => (
              <Box
                key={idx}
                width={`${100 / 24}%`}
                textAlign="center"
                fontSize={10}
                color={theme.palette.text.secondary}
              >
                {label}
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>

      {summary.map((item, idx) => (
        <Grid container spacing={1} key={idx} alignItems="center">
          <Grid item size={2.5}>
            <Typography>{item.vertical}</Typography>
          </Grid>
          <Grid item size={9.5}>
            <Box display="flex" position="relative">
              {HOUR_LABELS.map((_, hour) => {
                const inStart = Math.floor(item.inHour ?? -1);
                const outEnd = Math.ceil(item.outHour ?? -1);
                const isActive = hour >= inStart && hour < outEnd;

                return (
                  <Tooltip
                    key={hour}
                    title={
                      isActive && item.duration
                        ? `Start: ${item.inHour}h | End: ${item.outHour}h | Duration: ${item.duration}h`
                        : ""
                    }
                  >
                    <Box
                      width={`${100 / 24}%`}
                      height={20}
                      sx={{
                        backgroundColor: isActive
                          ? "rgba(76, 175, 80, 0.6)" // soft green
                          : "#f0f0f0",
                        border: "1px solid #ccc",
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </Grid>
        </Grid>
      ))}
    </Box>
  );
};

export default EstimatedWorkHoursChart;
